import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Button } from "../Button";

interface Country {
  name: string;
  code: string;
  countryCode: string;
  flag: string;
}

interface PhoneNumberInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange"
> {
  label?: string;
  value: string;
  error?: string | null;
  placeholder?: string;
  className?: string;
  faIcon?: IconProp;
  iconSrc?: string;
  iconText?: string;
  iconClassName?: string;
  isRequired?: boolean;
  disabled?: boolean;
  showCountryCode?: boolean;
  showCountryDropdown?: boolean;
  countryCode?: string;
  maxLength?: number;
  minLength?: number;
  onChange: (value: string) => void;
  onCountryChange?: (code: string) => void;
  onButtonClick?: () => void;
}

export function PhoneNumberInput({
  label,
  value,
  error,
  placeholder,
  className,
  faIcon,
  iconSrc,
  iconText,
  iconClassName,
  isRequired,
  disabled,
  showCountryCode = true,
  showCountryDropdown = true,
  countryCode,
  maxLength,
  minLength,
  onChange,
  onCountryChange,
  onButtonClick,
  ...props
}: PhoneNumberInputProps) {
  const countries: Country[] = [
    {
      name: "India",
      code: "IN",
      countryCode: "+91",
      flag: "/icons/ic-india.svg",
    },
  ];

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedCountry =
    countries.find((c) => c.countryCode === countryCode) ?? countries[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`flex flex-col gap-1 mb-4 ${className ?? ""}`}>
      {label && (
        <label className="text-[#A9A9A9] font-medium ml-1">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="flex">
        {showCountryDropdown && (
          <div ref={dropdownRef} className="relative">
            <Button
              type="button"
              disabled={disabled}
              onClick={() => setIsOpen((v) => !v)}
              className={`flex items-center gap-2 h-12 px-2
                         bg-black border border-[#333333] 
                         rounded-md text-white
                         ${error ? "border-red-500" : ""}`}
            >
              <img
                src={selectedCountry.flag}
                alt={selectedCountry.name}
                className="w-7 h-7 rounded-full"
              />
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`text-xs text-gray-400 transition ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </Button>

            {isOpen && (
              <div
                className="absolute z-50 mt-2 w-56 bg-black
                              border border-[#333333] rounded-lg shadow-xl"
              >
                {countries.map((country) => (
                  <Button
                    key={country.code}
                    type="button"
                    onClick={() => {
                      onCountryChange?.(country.countryCode);
                      setIsOpen(false);
                    }}
                    className="flex items-center gap-3 w-full px-4 py-3
                               text-left hover:bg-[#1D1D1D]"
                  >
                    <img
                      src={country.flag}
                      alt={country.name}
                      className="w-6 h-6 rounded-full"
                    />
                    <span className="text-white text-sm">
                      {country.name} ({country.countryCode})
                    </span>
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className={`relative flex-1 ml-2`}>
          {showCountryCode && value && (
            <div
              className="absolute left-2 top-1/2 -translate-y-1/2
                         text-white  opacity-80
                         pointer-events-none select-none"
            >
              {selectedCountry.countryCode}
            </div>
          )}

          <input
            {...props}
            type="tel"
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            minLength={minLength}
            className={`w-full h-12
                        bg-black border border-[#333333]
                        text-white focus:outline-none
                        focus:ring-2 focus:ring-[#333333]
                        rounded-lg
                        ${showCountryCode && value ? "pl-10 pr-4" : "px-4"}
                        ${error ? "border-red-500" : ""}`}
          />

          {faIcon && (
            <Button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#C71B4A99] flex items-center gap-1 text-sm"
              onClick={onButtonClick}
              disabled={onButtonClick ? false : true}
            >
              <div className="bg-black text-white p-2 hover:bg-[#C71B4A99]">
                <FontAwesomeIcon icon={faIcon} />
                {iconText && <span>{iconText}</span>}
              </div>
            </Button>
          )}
          {(iconSrc || iconText) && (
            <Button
              type="button"
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400  flex items-center gap-1 text-sm ${iconClassName || ""}`}
              onClick={onButtonClick}
              disabled={onButtonClick ? false : true}
            >
              {iconSrc && <img src={iconSrc} alt="icon" className="w-6 h-6" />}
              {iconText && <span>{iconText}</span>}
            </Button>
          )}
        </div>
      </div>
      {error && <div className="text-red-500 text-sm ml-1">{error}</div>}

    </div>
  );
}
